/**
 * The screener editor's Format tab — four sections, top to bottom (owner,
 * 2026-10-06, approved mock):
 *
 *   **Presets** fill the rule editor and save nothing on their own.
 *   **New rule** is that editor — one condition per line.
 *   **Apply to** is a checkbox list of every column, always in view.
 *   **Rules in use** groups the columns that carry identical rules, each with
 *   Edit (loads it back into the editor with its columns ticked) and ✕.
 *
 * **Nothing is saved until Apply.** The first version saved a single ticked
 * column on every keystroke, and the tab opened on the first column — so a
 * preset clicked before anything was chosen landed on Symbol. Opening with
 * nothing ticked and saving only on Apply removes that accident; editing one
 * column is Edit, change, Apply.
 *
 * Rules are still **copied onto each column** (`lib/format-rules.ts`), so a rule
 * set has no identity of its own: "the set on these 7 columns" is found by
 * equality every render, and editing it rewrites exactly those columns.
 *
 * Colouring one column by an *expression* is per column, not a rule for many,
 * so it lives with the column's other settings in the Columns tab.
 */

import { useMemo, useState } from "react";
import type { ScreenerColumn, ScreenerConfig, ScreenerFilterCondition, ScreenerFormatRule } from "@qufolio/model";
import { resolveColorRef } from "@qufolio/model";
import { formatTimeframe, type ScreenerColumnMeta } from "@qufolio/transport";
import { TokenSelect } from "./TokenSelect.js";
import { CONDITION_OPS, ConditionEditor, OP_LABEL } from "./ScreenerBuilder.js";
import { columnBand } from "../lib/bands.js";
import type { UniverseColumn } from "../lib/column-universe.js";
import type { TokenResolver } from "../lib/columns.js";
import {
  applyRules,
  FORMAT_PRESETS,
  FORMAT_TOKENS,
  hasNumberRules,
  removeRuleSet,
  ruleSetsInUse,
  type ApplyMode,
} from "../lib/format-rules.js";
import type { ScreenerConfigPatch } from "./ScreenerGrid.js";

export function FormatTab({
  config,
  universe,
  meta,
  onConfigChange,
  resolveToken,
}: {
  config: ScreenerConfig;
  universe: UniverseColumn[];
  meta: readonly ScreenerColumnMeta[];
  onConfigChange: (patch: ScreenerConfigPatch) => void;
  /** Kept for callers that still pass it; the expression editor moved to Columns. */
  compileExpr?: unknown;
  resolveToken?: TokenResolver;
}) {
  const columns = config.columns ?? [];
  // The island's swatches need a real colour; with no host resolver, the
  // contract's default theme — never a passthrough, which would hand CSS the
  // string "positive" and draw nothing (`lib/theme.ts`'s rule).
  const resolve = useMemo(
    () => resolveToken ?? ((token: string) => resolveColorRef(token) ?? token),
    [resolveToken],
  );

  const [draft, setDraft] = useState<ScreenerFormatRule[]>([]);
  // Nothing ticked on open — see the header for the accident this prevents.
  const [selected, setSelected] = useState<string[]>([]);
  const [mode, setMode] = useState<ApplyMode>("replace");
  /** The columns of the rule set being edited, or null for a new rule. */
  const [editing, setEditing] = useState<string[] | null>(null);

  const header = (field: string) => universe.find((u) => u.field === field)?.header ?? field;
  // The column the "row from another column" preset points at: the screen's
  // change column when it has one, else the first number.
  const rowField =
    universe.find((u) => /^changePercent/.test(u.field))?.field ??
    universe.find((u) => u.type === "number")?.field ??
    "";
  const numeric = hasNumberRules(draft);
  const isText = (field: string) => universe.find((u) => u.field === field)?.type === "text";
  // A text column cannot take a number rule; it stays unticked rather than
  // being written a rule that can never match (the stray rules on Symbol).
  const targets = selected.filter((f) => !(numeric && isText(f)));

  const reset = () => {
    setDraft([]);
    setSelected([]);
    setEditing(null);
  };

  const apply = () => {
    let next: ScreenerColumn[] = columns;
    // Editing a set: a column unticked from it loses the set, the rest are
    // rewritten — so the set ends on exactly the columns now ticked.
    if (editing) next = removeRuleSet(next, editing.filter((f) => !targets.includes(f)));
    next = applyRules(next, targets, draft, editing ? "replace" : mode);
    onConfigChange({ columns: next });
    reset();
  };

  const sets = ruleSetsInUse(columns);

  return (
    <>
      <Section title="Presets">
        <div style={ROW}>
          {FORMAT_PRESETS.map((preset) => (
            <button
              key={preset.id}
              type="button"
              title={preset.title}
              onClick={() => setDraft(preset.rules(rowField))}
              style={smallButton}
            >
              {preset.label}
            </button>
          ))}
        </div>
      </Section>

      <Section title={editing ? "Edit rules" : "New rule"} hint="the last matching condition wins">
        {draft.length === 0 && (
          <div style={{ ...HINT, marginBottom: "0.35rem" }}>Pick a preset above, or add a condition.</div>
        )}
        {draft.map((rule, i) => (
          <RuleRow
            key={i}
            rule={rule}
            target={rowField}
            meta={meta}
            resolve={resolve}
            onChange={(next) => setDraft(draft.map((r, j) => (j === i ? next : r)))}
            onRemove={() => setDraft(draft.filter((_, j) => j !== i))}
          />
        ))}
        <div style={ROW}>
          <button
            type="button"
            onClick={() => setDraft([...draft, { when: { op: "gt", value: 0 }, style: { fg: "positive" } }])}
            style={smallButton}
          >
            + condition
          </button>
          {(editing || draft.length > 0) && (
            <button type="button" onClick={reset} style={smallButton}>
              {editing ? "Cancel edit" : "Clear"}
            </button>
          )}
        </div>
      </Section>

      <Section title="Apply to">
        <ColumnList
          universe={universe}
          columns={columns}
          meta={meta}
          selected={selected}
          textLocked={numeric}
          onChange={setSelected}
        />
        <div style={{ ...ROW, marginTop: "0.4rem" }}>
          <button
            type="button"
            onClick={apply}
            disabled={draft.length === 0 || targets.length === 0}
            style={primaryButton}
          >
            Apply to {targets.length} column{targets.length === 1 ? "" : "s"}
          </button>
          {!editing && (
            <>
              <label style={RADIO} title="The rules take the place of each column's own">
                <input type="radio" checked={mode === "replace"} onChange={() => setMode("replace")} />
                Replace their rules
              </label>
              <label style={RADIO} title="The rules go after each column's own">
                <input type="radio" checked={mode === "add"} onChange={() => setMode("add")} />
                Add after
              </label>
            </>
          )}
        </div>
      </Section>

      <Section title="Rules in use">
        {sets.length === 0 && <div style={HINT}>No column has rules yet.</div>}
        {sets.map((set) => (
          <div key={set.fields.join("|")} style={SET_ROW}>
            <span style={{ flex: "1 1 auto", minWidth: 0 }}>
              {set.rules.map((rule) => ruleSummary(rule, header)).join(" · ")}
              <span style={HINT}> — on {set.fields.map(header).join(", ")}</span>
            </span>
            <button
              type="button"
              aria-label={`Edit the rules on ${set.fields.map(header).join(", ")}`}
              onClick={() => {
                setDraft(set.rules.map((r) => ({ ...r })));
                setSelected([...set.fields]);
                setEditing([...set.fields]);
              }}
              style={smallButton}
            >
              Edit
            </button>
            <button
              type="button"
              aria-label={`Remove the rules on ${set.fields.map(header).join(", ")}`}
              onClick={() => onConfigChange({ columns: removeRuleSet(columns, set.fields) })}
              style={smallButton}
            >
              ✕
            </button>
          </div>
        ))}
      </Section>
    </>
  );
}

/** A titled block — the four sections read as four steps. */
function Section({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <section style={SECTION}>
      <div style={SECTION_TITLE}>
        {title}
        {hint && <span style={{ ...HINT, textTransform: "none", letterSpacing: 0 }}> — {hint}</span>}
      </div>
      {children}
    </section>
  );
}

/** One rule in words, for Rules in use: `> 0 Positive`, `%Chg < 0 Negative fill bold`. */
function ruleSummary(rule: ScreenerFormatRule, header: (field: string) => string): string {
  const when = rule.when as { op?: string; value?: unknown; valueTo?: unknown; field?: string };
  const label = (token?: string) => FORMAT_TOKENS.find((t) => t.token === token)?.label ?? token ?? "";
  const op = OP_LABEL[(when.op ?? "") as keyof typeof OP_LABEL] ?? when.op ?? "";
  const value =
    when.op === "blank" || when.op === "notBlank"
      ? ""
      : when.op === "between"
        ? ` ${String(when.value)}–${String(when.valueTo)}`
        : ` ${String(when.value ?? "")}`;
  const subject = when.field ? `${header(when.field)} ` : "";
  const style = [
    rule.style.fg ? label(rule.style.fg) : "",
    rule.style.bg ? `${label(rule.style.bg)} fill` : "",
    rule.style.bold ? "bold" : "",
  ]
    .filter(Boolean)
    .join(" ");
  return `${subject}${op}${value} ${style}`.trim();
}

/**
 * Every column as a checkbox, always in view (owner: "a list, not a dropdown"),
 * with search and quick picks.
 *
 * **A column coloured by an expression is shown but never ticked by a quick
 * pick**, and is marked: its `colorBy` overrides every rule, so applying rules
 * to it changes nothing on screen. **A text column is greyed while the rules
 * compare numbers** — Symbol and Name cannot be "> 0".
 */
function ColumnList({
  universe,
  columns,
  meta,
  selected,
  textLocked,
  onChange,
}: {
  universe: UniverseColumn[];
  columns: readonly ScreenerColumn[];
  meta: readonly ScreenerColumnMeta[];
  selected: string[];
  textLocked: boolean;
  onChange: (next: string[]) => void;
}) {
  const [query, setQuery] = useState("");
  const metaByField = useMemo(() => new Map(meta.map((m) => [m.field, m])), [meta]);
  const byField = useMemo(() => new Map(columns.map((c) => [c.field, c])), [columns]);
  const overridden = (field: string) => byField.get(field)?.colorBy !== undefined;
  const locked = (u: UniverseColumn) => textLocked && u.type === "text";
  const bandOf = (field: string) => columnBand(byField.get(field), metaByField.get(field))?.name;
  const timeframeOf = (u: UniverseColumn) => (u.timeframe ? formatTimeframe(u.timeframe) : "");

  const bands = [...new Set(universe.map((u) => bandOf(u.field)).filter((b): b is string => !!b))];
  const timeframes = [...new Set(universe.map(timeframeOf).filter(Boolean))];

  const q = query.trim().toLowerCase();
  const shown = universe.filter(
    (u) =>
      !q ||
      u.field.toLowerCase().includes(q) ||
      u.header.toLowerCase().includes(q) ||
      u.description.toLowerCase().includes(q),
  );

  const pick = (fields: string[]) =>
    onChange([...selected, ...fields.filter((f) => !selected.includes(f) && !overridden(f))]);
  const toggle = (field: string) =>
    onChange(selected.includes(field) ? selected.filter((f) => f !== field) : [...selected, field]);

  return (
    <div>
      <div style={{ ...ROW, marginBottom: "0.3rem" }}>
        <input
          aria-label="Search columns"
          placeholder="search columns…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          style={{ ...inputStyle, width: 140 }}
        />
        <button
          type="button"
          onClick={() => pick(universe.filter((u) => u.type === "number").map((u) => u.field))}
          style={smallButton}
        >
          All numeric
        </button>
        {bands.map((band) => (
          <button
            key={band}
            type="button"
            onClick={() => pick(universe.filter((u) => bandOf(u.field) === band).map((u) => u.field))}
            style={smallButton}
          >
            {band}
          </button>
        ))}
        {timeframes.length > 0 && (
          <select
            aria-label="Timeframe"
            value=""
            onChange={(e) =>
              e.target.value && pick(universe.filter((u) => timeframeOf(u) === e.target.value).map((u) => u.field))
            }
            style={inputStyle}
          >
            <option value="">Timeframe…</option>
            {timeframes.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        )}
        <button type="button" onClick={() => onChange([])} style={smallButton}>
          Clear
        </button>
      </div>
      <div style={LIST}>
        {shown.map((u) => (
          <label
            key={u.field}
            style={{ ...CHECK, opacity: locked(u) ? 0.45 : 1 }}
            title={locked(u) ? "A text column cannot take a number rule" : u.description || u.field}
          >
            <input
              type="checkbox"
              disabled={locked(u)}
              checked={selected.includes(u.field) && !locked(u)}
              onChange={() => toggle(u.field)}
            />
            <span>{u.header}</span>
            {u.type === "text" && <span style={HINT}>text</span>}
            {overridden(u.field) && <span style={HINT}>colorBy overrides</span>}
          </label>
        ))}
      </div>
    </div>
  );
}

/** One condition: whose value, the comparison, then text, fill and bold — on one line. */
function RuleRow({
  rule,
  target,
  meta,
  resolve,
  onChange,
  onRemove,
}: {
  rule: ScreenerFormatRule;
  target: string;
  meta: readonly ScreenerColumnMeta[];
  resolve: (token: string) => string;
  onChange: (next: ScreenerFormatRule) => void;
  onRemove: () => void;
}) {
  const rowScoped = "field" in rule.when || "children" in rule.when;
  const when = rule.when as { op: string; value?: unknown };
  const needsValue = when.op !== "blank" && when.op !== "notBlank";
  return (
    <div style={RULE_ROW}>
      <select
        aria-label="Rule scope"
        value={rowScoped ? "row" : "cell"}
        onChange={(e) =>
          onChange({
            ...rule,
            when:
              e.target.value === "row"
                ? ({ field: target, op: "lt", value: 0 } as ScreenerFilterCondition)
                : { op: "lt", value: 0 },
          })
        }
        style={inputStyle}
      >
        <option value="cell">If value</option>
        <option value="row">If column</option>
      </select>

      {rowScoped ? (
        <ConditionEditor
          condition={rule.when as ScreenerFilterCondition}
          meta={meta}
          onChange={(next) => onChange({ ...rule, when: next })}
        />
      ) : (
        <>
          <select
            aria-label="Rule comparison"
            value={when.op}
            onChange={(e) => onChange({ ...rule, when: { ...rule.when, op: e.target.value } as never })}
            style={inputStyle}
          >
            {CONDITION_OPS.map((op) => (
              <option key={op} value={op}>
                {OP_LABEL[op]}
              </option>
            ))}
          </select>
          {needsValue && (
            <input
              aria-label="Rule value"
              value={String(when.value ?? "")}
              onChange={(e) => {
                const raw = e.target.value;
                const n = Number(raw);
                const value =
                  raw === "true" ? true : raw === "false" ? false : raw !== "" && !Number.isNaN(n) ? n : raw;
                onChange({ ...rule, when: { ...rule.when, value } as never });
              }}
              style={{ ...inputStyle, width: 64 }}
            />
          )}
        </>
      )}

      <span style={HINT}>→ Text</span>
      <TokenSelect
        label="Rule text colour"
        value={rule.style.fg}
        resolve={resolve}
        onChange={(fg) => onChange({ ...rule, style: { ...rule.style, fg } })}
      />
      <span style={HINT}>Fill</span>
      <TokenSelect
        label="Rule fill colour"
        value={rule.style.bg}
        resolve={resolve}
        onChange={(bg) => onChange({ ...rule, style: { ...rule.style, bg } })}
      />
      <label style={{ fontSize: "0.74rem", color: "var(--qf-text-secondary)" }}>
        <input
          type="checkbox"
          checked={rule.style.bold === true}
          onChange={(e) => onChange({ ...rule, style: { ...rule.style, bold: e.target.checked } })}
        />{" "}
        Bold
      </label>
      <button type="button" aria-label="Remove rule" onClick={onRemove} style={smallButton}>
        ✕
      </button>
    </div>
  );
}

// ── chrome — the builder's own values, kept local so this file stands alone ──

const ROW: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "0.4rem",
  marginBottom: "0.5rem",
  flexWrap: "wrap",
};


const HINT: React.CSSProperties = { fontSize: "0.72rem", color: "var(--qf-text-muted)" };


const RADIO: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 3,
  fontSize: "0.74rem",
  color: "var(--qf-text-secondary)",
};

const CHECK: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "0.4rem",
  fontSize: "0.76rem",
  padding: "0.1rem 0",
  cursor: "pointer",
};

const SECTION: React.CSSProperties = {
  marginBottom: "0.7rem",
  paddingBottom: "0.55rem",
  borderBottom: "1px solid var(--qf-border)",
};

const SECTION_TITLE: React.CSSProperties = {
  fontSize: "0.66rem",
  fontWeight: 600,
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  color: "var(--qf-text-secondary)",
  marginBottom: "0.35rem",
};

const RULE_ROW: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "0.3rem",
  marginBottom: "0.3rem",
  flexWrap: "wrap",
};

const SET_ROW: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "0.35rem",
  fontSize: "0.76rem",
  padding: "0.2rem 0",
};

const LIST: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(8.5rem, 1fr))",
  columnGap: "0.6rem",
  maxHeight: 180,
  overflowY: "auto",
  padding: "0.25rem 0.35rem",
  border: "1px solid var(--qf-border)",
  borderRadius: 6,
};


const smallButton: React.CSSProperties = {
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  padding: "0.05rem 0.35rem",
  background: "transparent",
  color: "var(--qf-text-secondary)",
  cursor: "pointer",
  fontSize: "0.74rem",
};

const primaryButton: React.CSSProperties = {
  ...smallButton,
  background: "var(--qf-accent)",
  borderColor: "var(--qf-accent)",
  color: "var(--qf-background)",
};

const inputStyle: React.CSSProperties = {
  padding: "0.15rem 0.3rem",
  background: "var(--qf-background)",
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  color: "var(--qf-text)",
  fontSize: "0.76rem",
};

